
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import { ReactNode, useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, CardText, Col, Row } from "reactstrap";
import { AnyAction, Dispatch } from "redux";
import { IProject, IProjectCreation } from "@api/schema";
import BaseLayout from "@components/BaseLayout";
import Icon from "@components/common/Icon";
import PageNavigation from "@components/common/PageNavigation";
import TranslatedHtml from "@components/common/TranslatedHtml";
import ErrorPage from "@components/ErrorPage";
import ProjectCard from "@components/marketplace/projects/ProjectCard";
import IdeaForm from "@components/project/IdeaForm";
import OnboardingComponent from "@components/usecases/onboarding/OnboardingComponent";
import { createIdeaAction } from "@redux/actions/idea";
import { loadCurrentProcessAction, ProcessActionTypes } from "@redux/actions/processes";
import { IFormikActions } from "@redux/helper/actions";
import { AppState } from "@redux/reducer";
import { selectIsAuthenticated } from "@redux/reducer/auth";
import { selectCurrentProcess, selectSingleEntityUsecaseState } from "@redux/reducer/data";
import { EntityType } from "@redux/reduxTypes";
import { addNewIdeaOnboardingAction, OnboardingType, selectOtherExistingOnboardingType } from "@redux/usecases/onboarding";
import { FreshIdea } from "@services/projectHelper";
import { Routes, routeWithParams } from "@services/routes";
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    createIdea: (idea: IProjectCreation, actions: IFormikActions) => dispatch(createIdeaAction(idea, actions)),
    addOnboardingIdea: (idea: IProjectCreation) => dispatch(addNewIdeaOnboardingAction(idea)),
});
const mapStateToProps = (state: AppState) => ({
    onboardingIdea: state.onboardingData.newIdea,
    isAuthenticated: selectIsAuthenticated(state),
    currentProcess: selectCurrentProcess(state),
    processRequest: selectSingleEntityUsecaseState(state, EntityType.Process, ProcessActionTypes.LoadCurrentProcess),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PageProps = ConnectedProps<typeof connector>;
/**
 * This page allows to input a new project idea
 */
const IdeaCreationPage: React.FC<PageProps> = (props: PageProps) => {
    const { createIdea, onboardingIdea: createdOnboardingIdea, isAuthenticated, addOnboardingIdea, currentProcess, processRequest } = props;
    const { t } = useTranslation("common");
    const [createdAuthenticatedIdea, setCreatedAuthenticatedIdea] = useState<IProject>(null);
    const dispatch = useDispatch();
    const thisOnboardingType = OnboardingType.NewIdea;
    const otherExistingOnboardingType = useSelector((state: AppState) => selectOtherExistingOnboardingType(state, thisOnboardingType));
    useEffect(() => {
        if (!currentProcess && !processRequest?.isLoading) {
            dispatch(loadCurrentProcessAction());
        }
    }, []);
    if ((processRequest?.loaded && !currentProcess) || processRequest?.loadingError) {
        return <ErrorPage statusCode={404} error={"failure.noProcess"}/>;
    }
    // on submitting the idea is stored in the state
    // to allow the user to login or register afterwards
    const onSubmit = (values: IProjectCreation, actions: IFormikActions): void => {
        // inject the current process, it's required
        values.process = currentProcess['@id'];
        if (isAuthenticated) {
            actions.onSuccess = (result: IProject) => {
                setCreatedAuthenticatedIdea(result);
            };
            createIdea(values, actions);
        }
        else {
            /**
             * Stores in the onboarding state that a idea has been created.
             * E.g. the user created an idea before logging in => the log-in page checks the state and redirects for idea creation.
             * Hint: any idea is always connected to one user
             */
            addOnboardingIdea(values);
        }
    };
    // function to reset the idea to input a new one
    const resetIdea = () => {
        setCreatedAuthenticatedIdea(null);
    };
    return <BaseLayout pageTitle={t("page.ideas.create.title")}>
    <Row>
      <Col>
        <h1>{t("page.ideas.create.heading")}</h1>
        <p><TranslatedHtml content="page.ideas.create.intro"/></p>
      </Col>
    </Row>
    <Row>
      <Col>
        <PageNavigation leftNavigation={{ href: Routes.Marketplace, title: "goto.marketplace" }}/>
      </Col>
    </Row>
    <Row>
      <Col sm={8}>
        <Card>
          <CardBody>
            {!createdAuthenticatedIdea && !createdOnboardingIdea && !otherExistingOnboardingType &&
            <IdeaForm onSubmit={onSubmit} idea={FreshIdea}/>}

            <OnboardingComponent thisOnboardingType={thisOnboardingType} creationSuccessUI={(object: IProject): ReactNode => {
            return <ProjectCard project={object}/>;
        }} createdAuthenticatedObject={createdAuthenticatedIdea} authenticatedCreationThanksMessageI18nKey="page.ideas.create.thanksForCreatingIdea" authenticatedCreationInfoMessageI18nKey="page.ideas.create.ideaSaved" 
    /**
     * NOTE on React/design and on the extra "guard" for `createdAuthenticatedIdea`:
     * Although `OnboardingComponent` shows `authenticatedCreationAdditionalUI`
     * only if `createdAuthenticatedObject` is non-null,
     * the code without the extra guard here would lead to an *undefined* error
     * when accessing `createdAuthenticatedIdea.id`.
     *
     * Obviously the extra guard here ensures that the ReactNode is re-evaluated
     * after changes on `createdAuthenticatedIdea`.
     */
    authenticatedCreationAdditionalUI={createdAuthenticatedIdea &&
            <>
                  <CardText className="text-center">
                    <Link href={routeWithParams(Routes.CreateProject, { id: createdAuthenticatedIdea.id })}>
                      <a className="btn primary-btn">{t("goto.newProject")}</a>
                    </Link>
                  </CardText>
                  <CardText className="text-center">
                    <Button onClick={resetIdea}>{t("page.ideas.create.addOtherIdea")}</Button>
                  </CardText>
                  <CardText className="text-center">
                    <Link href={Routes.Marketplace}>
                      <a className="btn page-navigation-btn"><Icon name="arrow-left" size={20}/>{t("goto.marketplace")}</a>
                    </Link>
                  </CardText>
                </>} 
    // NOTE special handling of "currentSourceObject/onboardingObjectIsFromAnotherSource" params,
    // because idea creation is always unique
    currentSourceObjectIsSourceForOnboardingObject={true} createdOnboardingObject={createdOnboardingIdea} onboardingCreationThanksMessageI18nKey="page.ideas.create.thanksForOnboardingIdea" onboardingCreationInfoMessageI18nKey="page.ideas.create.ideaNotSaved"/>
          </CardBody>
        </Card>
      </Col>
    </Row>
  </BaseLayout>;
};
const __Next_Translate__Page__18c4f5171ac__ = connector(IdeaCreationPage);

    export default __appWithI18n(__Next_Translate__Page__18c4f5171ac__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  